<script>
  import { link } from '../svelte-navigator';
  import { Loading, Tabs, UserButton, AddButton, IconButton, TextField2, Grid, RouteCell, TagsCell, AnchorCell, DoubleSparkline, SparklineCell, StatusCountCell, InfoDialog, QueryDialog, Notification } from '../components';
  import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/';
  import { user, readonly, search, queries, queryResults, history, macroHistory } from '../stores.js';
  import { subscribe, unsubscribe } from '../api.js';
  import { loadQueries, deleteQuery, loadHistory, loadMacroHistory, getLifecycleStatusCount, getComplianceStatusCount } from '../data.js';
  import { getWhen, weekFormatter, getTrend } from '../utils.js';
  import NotificationFilter from "carbon-icons-svelte/lib/NotificationFilled20";
  import NotificationOffFilter from "carbon-icons-svelte/lib/NotificationOffFilled20";

  const lifeCycleStatusLabels = { 
    good: "active", 
    suspect: "NRND or EOL",
    bad: "obsolete or discontinued @ Digi-Key",
    unknown: "unknown"
  };

  const complianceStatusLabels = { 
    good: "compliant", 
    bad: "non-compliant",
    unknown: "unknown"
  };

  const statusColumns = [
    { name: "Name", value: x => x.Name, component: RouteCell, props: x => ({ href: `/history/${x.ID}?n=20` }) },
    { name: "Ax No", value: x => x.AxNo || "" },
    { name: "Datasheet", value: x => x.Datasheet ? "Datasheet" : "", component: AnchorCell, props: x => ({ href: x.Datasheet })},
    { name: "Tags", value: x => x.Tags, component: TagsCell, formatter: tags => tags ? tags.split(",").map(x => "#" + x).join(",") : "", clickHandler: e => $search = e.detail.tag },
    { name: "Last Order", value: x => x.AvailabilityInHouse || 0, extra: x => x.AvailabilityInHouse && x.AvailabilityInHouseDate ? getWhen(x.AvailabilityInHouseDate, false) : "" },
    { name: "Stock", value: x => x.Availability, component: SparklineCell, props: x => ({ trend: x.AvailabilityTrend?.data, color: x.AvailabilityTrend?.color }) },
    { name: "Price", value: x => x.Price, formatter: x => x !== null ? "$" + x : x, component: SparklineCell, props: x => ({ trend: x.PriceTrend?.data, color: x.PriceTrend?.color, tooltipFormatter: p => "$" + p.toPrecision(4)/1 }) },
    { name: "Avg Lead Time", value: x => x.AvgLeadTime, formatter: x => weekFormatter(x), component: SparklineCell, props: x => ({ trend: x.AvgLeadTimeTrend?.data, color: x.AvgLeadTimeTrend?.color, tooltipFormatter: t => weekFormatter(t) }) },
    { name: "Lifecycle Status", value: x => getLifecycleStatusCount(x.LifecycleStatus), component: StatusCountCell, props: () => ({ labels: lifeCycleStatusLabels }), alert: x => x.LifecycleStatusFlagged, alertTooltip: "Decrease in good (active) vs bad (other) lifecycle statuses, or only unknown statuses and stock < 5000." },
    { name: "RoHS", value: x => getComplianceStatusCount(x.RoHS), component: StatusCountCell, props: () => ({ labels: complianceStatusLabels }) },
    { name: "Manufacturers", value: x => x.Manufacturers || "" },
    { name: "Comments", value: x => x.Comments || "", extra: x => x.Comments ? x.CommentsDate : "" },
    { name: "Attention", value: x => x.Attention || "", extra: x => x.Attention ? x.AttentionDate : "" }
  ];

  const editColumns = [
    { name: "Name", value: x => x.Name, component: RouteCell, props: x => ({ href: `/history/${x.PartitionKey}` }) },
    { name: "Ax No", value: x => x.AxNo || "" },
    { name: "Tags", value: x => x.Tags, component: TagsCell, formatter: tags => tags ? tags.split(",").map(x => "#" + x).join(",") : "", clickHandler: e => $search = e.detail.tag },
    { name: "Search Query", value: x => x.SearchQuery },
    { name: "MPN Filter", value: x => x.MpnFilter },
    { name: "Interval", value: x => getInterval(x.Interval, x.NotifyEveryIncrease) },
    { name: "Last Order", value: x => x.InHouseStock || 0, extra: x => x.InHouseStockDate ? getWhen(x.InHouseStockDate, false) : "" },
    { name: "Comments", value: x => x.Comments || "", extra: x => x.Comments ? x.CommentsDate : "" },
    { name: "Attention", value: x => x.Attention || "", extra: x => x.Attention ? (x.AttentionDate || "") : "" },
    { name: "Last Edited By", value: x => getName(x.LastEditedBy) }
  ];

  let selectedTab = "STATUS";
  let queryDialog;
  let notification;
  let showInfo = false;

  $: status = getStatus($history, $queries);
  $: lastUpdate = getWhen(Math.max.apply(null, $history.map(d => Number(d.History.last().RowKey) * 1000)));

  $: macroHistoryGe50 = $macroHistory.map(h => ({ x: h.UnixTimeSeconds, y: h.CountStockGe50 }));
  $: macroHistoryLt50 = $macroHistory.map(h => ({ x: h.UnixTimeSeconds, y: h.CountStockLt50 }));

  function getStatus(history, queries) {
    let statuses = [];
    for (let query of queries) {
      let queryHistory = history.find(x => x.ID === query.PartitionKey);
      statuses.push({
        ID: query.PartitionKey,
        Name: query.Name,
        Datasheet: query.Datasheet,
        Tags: query.Tags,
        Timestamp: queryHistory ? Number(queryHistory.History.last().RowKey) * 1000 : null,
        AvailabilityInHouse: query.InHouseStock || 0,
        AvailabilityInHouseDate: query.InHouseStockDate,
        Availability: queryHistory ? queryHistory.History.last().TotalAvailable : null,
        AvailabilityTrend: queryHistory ? getTrend(queryHistory.History, "TotalAvailable", queryHistory.AvailabilityFlagged) : null,
        Price: queryHistory ? queryHistory.History.last().MedianUnitPrice?.toPrecision(4)/1 || "?" : null,
        PriceTrend: queryHistory ? getTrend(queryHistory.History, "MedianUnitPrice", queryHistory.PriceFlagged) : null,
        MinLeadTime: queryHistory ? queryHistory.History.last().MinLeadTimeDays : null,
        MinLeadTimeTrend: queryHistory ? getTrend(queryHistory.History, "MinLeadTimeDays", queryHistory.MinLeadTimeFlagged) : null,
        AvgLeadTime: queryHistory ? queryHistory.History.last().AvgLeadTimeDays : null,
        AvgLeadTimeTrend: queryHistory ? getTrend(queryHistory.History, "AvgLeadTimeDays", queryHistory.AvgLeadTimeFlagged) : null,
        MaxLeadTime: queryHistory ? queryHistory.History.last().MaxLeadTimeDays : null,
        MaxLeadTimeTrend: queryHistory ? getTrend(queryHistory.History, "MaxLeadTimeDays", queryHistory.MaxLeadTimeFlagged) : null,
        LifecycleStatus: queryHistory ? JSON.parse(queryHistory.History.last().LifecycleStatus) : null,
        LifecycleStatusFlagged: queryHistory ? queryHistory.LifecycleStatusFlagged : null,
        RoHS: queryHistory ? JSON.parse(queryHistory.History.last().RoHS) : null,
        Manufacturers: query.Manufacturers || "",
        Comments: query.Comments || "",
        CommentsDate: query.CommentsDate || "",
        Attention: query.Attention || "",
        AttentionDate: query.AttentionDate || "",
        Subscribed: query.Subscribed
      });
    }

    return statuses;
  }

  function getName(user) {
		if (user?.includes("@")) return user.match(/(.+)@/)[1];
		return user;
	}

  function getInterval(interval, everyIncrease) {
    let intervalString = interval;
    if (everyIncrease) intervalString += " + inc";
    return intervalString;
  }

  function edit(data) {
    queryDialog.show($queries.find(x => x.PartitionKey === data.PartitionKey));
  }

  function onQuerySaved(response) {
    if (response.error) {
      notification.show("error", response.error);
    } else {
      let query = response.data;
      if (response.edit) {
        let index = $queries.findIndex(x => x.PartitionKey === query.PartitionKey);
        $queries[index] = query;
        $queryResults[query.PartitionKey] = null;
      } else {
        query.Subscribed = true;
        subscribe([query.PartitionKey]);
        $queries.push(query);
        $queries = $queries; // Force update
      }
    }
  }

  async function onDeleteQuery(data) {
    let query = $queries.find(x => x.PartitionKey === data.PartitionKey);
    if(!confirm(`Are you sure you want to delete '${query.Name}'?`)) return;

    await deleteQuery(query.PartitionKey);
    await unsubscribe([query.PartitionKey]);
    let index = $queries.findIndex(x => x.PartitionKey === query.PartitionKey);
    $queries.splice(index, 1);
    $queries = $queries; // Force update
  }

  async function onStarClicked(e)
  {
    let query = $queries.find(x => x.PartitionKey == e.detail.data.ID);
    if (!query.Subscribed) {
      const response = await subscribe([query.PartitionKey]);
      if (response.ok) {
        query.Subscribed = true;
      } else {
        notification.show("error", response.error);
      }
    } else {
      const response = await unsubscribe([query.PartitionKey]);
      if (response.ok) {
        query.Subscribed = false;
      } else {
        notification.show("error", response.error);
      }
    } 
    $queries = $queries; // force update
  }

  async function onStarHeaderClicked(e) {
    const oldQueries = JSON.parse(JSON.stringify($queries));
    for (let component of e.detail.data) {
      let query = $queries.find(x => x.PartitionKey == component.ID);
      query.Subscribed = e.detail.star;
    }
    $queries = $queries; // force update

    const ids = e.detail.data.map(x => x.ID);
    if (e.detail.star) {
      const response = await subscribe(ids);
      if (response.error) {
        notification.show("error", response.error);
        $queries = oldQueries;
      }
    } else {
      const response = await unsubscribe(ids);
      if (response.error) {
        notification.show("error", response.error);
        $queries = oldQueries;
      }
    }
  }

  async function loadData() {
    if ($queries.length === 0) {
      await Promise.all([loadQueries(), loadHistory(), loadMacroHistory()]).catch(console.error);
    }
  }
</script>

<div class="container">
  <div class="toolbar">
    <UserButton user={$user.userDetails} />
    {#if !$readonly}<Tabs tabs={[ "STATUS", "EDIT" ]} bind:selectedTab />{/if}
    {#if selectedTab === "EDIT"}<AddButton title="New query" on:click={() => queryDialog.show()} />{/if}
    <div class="search">
      <TextField2 placeholder="search" bind:text={$search} clearButton width="300px" height="32px" />
      <NotificationFilter title="Show only subscribed" style="fill: #9d4b4b; cursor: pointer; margin: 0 4px 0 8px;" on:click={() => $search = "[subscribed]"} />
      <NotificationOffFilter title="Show only not subscribed" style="fill: #aaa; cursor: pointer; margin: 0 8px 0 4px;" on:click={() => $search = "[!subscribed]"} />
    </div>
    {#if selectedTab === "STATUS"}
      <IconButton icon={faInfoCircle} color="#3574b6" size="1.5x" on:click={() => showInfo = true} />
    {/if}
    <span>Last updated: {lastUpdate}</span>
    <a href="/macrohistory" use:link><DoubleSparkline data1={macroHistoryGe50} data2={macroHistoryLt50} tooltip="Green: components with stock >= 50, red: components with stock < 50" /></a>
  </div>
  {#await loadData()}
    <Loading visible />
    <Grid columns={statusColumns} />
  {:then}
    <Grid data={status} columns={statusColumns} banded sortable subscribable sortCol="Name" search={$search} visible={selectedTab === "STATUS"} on:starClicked={onStarClicked} on:starHeaderClicked={onStarHeaderClicked} />
    <Grid data={$queries} columns={editColumns} banded sortable sortCol="Name" editable editOnLeft deletable search={$search} visible={selectedTab === "EDIT"} on:edit={(e) => edit(e.detail.data)} on:delete={(e) => onDeleteQuery(e.detail.data)} />
  {:catch error}
    <Notification icon="error" message={error.message} visible />
  {/await}
</div>
<InfoDialog bind:visible={showInfo} />
<QueryDialog bind:this={queryDialog} on:saved={(e) => onQuerySaved(e.detail)} />
<Notification bind:this={notification} />

<style>
  .container {
    display: grid;
    grid-template-rows: min-content auto;
    gap: 5px;
    width: 100%;
    height: calc(100vh - 10px);
  }

  .toolbar {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    padding-left: 5px;
  }

  .search {
    display: flex; 
    flex-direction: row; 
    background-color: #efefef;
    border-width: 1px 1px 1px 0;
    border-style: solid;
    border-color: #ccc;
    border-radius: var(--border-radius);
    align-items: center;
    box-sizing: border-box;
    height: 32px;
  }

  span {
    color: #666;
    user-select: none;
  }
</style>